import Axios, { AxiosError } from 'axios';
import React, { SyntheticEvent } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import validate from 'validate.js';
import { passwordRecovery } from '../../constraints/PasswordRecovery';
import { useMergedState } from '../../hooks/useMergedState';
import { IBaseForm, IBaseState } from '../../types/types';
import Alert, { AlertType } from '../components/Alert';
import AuthLogo from '../components/AuthLogo';
import { UseTranslate } from '../../hooks/Translate';

interface IPasswordRecoveryState extends IBaseForm {
  focus: Record<string, boolean>;
}

type PasswordRecoveryState = Partial<IBaseState & IPasswordRecoveryState>;

type Params = {
  userId: string;
};

export const PasswordRecovery = () => {
  const [state, setState] = useMergedState<PasswordRecoveryState>({
    errors: {},
    focus: {},
    validated: false,
    alertProps: {
      type: AlertType.NONE,
    },
  });

  const navigate = useNavigate();
  const [translate] = UseTranslate();

  const { userId } = useParams<Params>();

  const confirmPasswordRecovery = async (data: any) => {
    try {
      const url = `${process.env.REACT_APP_CAS_API}/users/confirmPasswordRecovery/${userId}`;
      const res = await Axios.put(url, data);

      if (res.data.status === 'SUCCESS') {
        navigate(`/recovery-success`);
      }
    } catch (e) {
      let message;
      if (e instanceof AxiosError && e.response) {
        message = e.response.data.message
      }
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.registration.registerError'),
          message: message === 'INVALID_OTP' ? translate('cas.registration.INVALID_OTP') : message,
        },
      });
    }
  };

  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();

    const fd = new FormData(e.target as HTMLFormElement);
    const values = Object.fromEntries<any>(fd.entries());

    const result = validate(values, passwordRecovery(
      translate('cas.registration.pswdMin6Char'),
      translate('cas.registration.pswdDontMatch')
    ), { fullMessages: false });

    if (result) {
      setState({ errors: result });
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.registration.registerError'),
          message: translate('cas.registration.registerCheckErrorFields'),
        },
      });
    } else {
      setState({ errors: {} });
      confirmPasswordRecovery(values);
    }

    setState({ validated: true });
  };

  const validatePassword = () => {
    const elements: { [key: string]: HTMLInputElement } = {};
    const attributes: { [key: string]: string } = {};
    const _constraints: { [key: string]: object } = {};

    for (const key of ['password', 'repeatPassword']) {
      const selector = '[name="' + key + '"]';
      const element = document.querySelector(selector) as HTMLInputElement;
      if (state.focus![key]) {
        elements[key] = element;
        attributes[key] = element.value;
        _constraints[key] = passwordRecovery(
          translate('cas.registration.pswdMin8Char'),
          translate('cas.registration.pswdDontMatch')
        )[key];
      }
    }

    const result = validate(attributes, _constraints, { fullMessages: false });

    return {
      elements,
      attributes,
      result,
    };
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setState({
      focus: {
        ...state.focus,
        [e.target.name]: true,
      },
    });
  };

  const onChangePassword = () => {
    const { result, elements } = validatePassword();

    setState({
      errors: {
        ...state.errors,
        ...result,
      },
    });

    for (const key in elements) {
      if (result && typeof result[key] === 'object') {
        elements[key].classList.add('is-invalid');
      } else {
        elements[key].classList.remove('is-invalid');
      }
    }
  };

  const onChangeRepeatPassword = () => {
    const { result, elements } = validatePassword();

    setState({
      errors: {
        ...state.errors,
        repeatPassword: result?.repeatPassword,
      },
    });

    if (typeof result?.repeatPassword === 'object') {
      elements.repeatPassword.classList.add('is-invalid');
    } else {
      elements.repeatPassword.classList.remove('is-invalid');
    }
  };

  const togglePassword = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.currentTarget.classList.toggle('toggle-visible-password--visible');
    const previousSibling = e.currentTarget.previousSibling as HTMLInputElement;
    const type = previousSibling.getAttribute('type');
    switch (type) {
      case 'text':
        previousSibling.setAttribute('type', 'password');
        break;
      case 'password':
        previousSibling.setAttribute('type', 'text');
        break;
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="text-center">
        <AuthLogo />
      </div>

      <div className="mb-4">
        <div className="small">
          <h6>{translate('cas.registration.changePswd')}</h6>
        </div>
      </div>

      <Alert
        type={state.alertProps!.type}
        title={state.alertProps!.title}
        message={state.alertProps!.message}
      />
      <Form validated={state.validated} onSubmit={onFormSubmit} noValidate>
        <Form.Group className='mb-3 position-relative' controlId="password">
          <Form.Label className="small password-icon">{translate('cas.registration.pswd')}</Form.Label>
          <Form.Control
            type="password"
            name="password"
            placeholder={translate('cas.registration.inputPswd')}
            autoComplete="current-password"
            isInvalid={!!state.errors?.password}
            onFocus={onFocus}
            onChange={onChangePassword}
            required
          />
          <span className="toggle-visible-password" onClick={togglePassword} />
          <Form.Control.Feedback type="invalid">
            {state.errors?.password && state.errors?.password[0]}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className='mb-3 position-relative' controlId="repeat-password">
          <Form.Label className="small password-icon">
            {translate('cas.registration.rptPswd')}
          </Form.Label>
          <Form.Control
            type="password"
            name="repeatPassword"
            placeholder={translate('cas.registration.inputPswd')}
            autoComplete="repeat-password"
            isInvalid={!!state.errors?.repeatPassword}
            onFocus={onFocus}
            onChange={onChangeRepeatPassword}
            required
          />
          <span className="toggle-visible-password" onClick={togglePassword} />
          <Form.Control.Feedback type="invalid">
            {state.errors?.repeatPassword && state.errors?.repeatPassword[0]}
          </Form.Control.Feedback>
        </Form.Group>

        <Button variant="primary" type="submit" className="w-100">
          {translate('cas.registration.next')}
        </Button>
      </Form>
    </div>
  );
};
